import React from 'react';
import { 
    Grid, 
    Card, 
    CardContent, 
    Typography 
} from '@mui/material';
import DocumentUploadSection from 'components/onboardingForms/DocumentUploadSection';

export const DocumentsAndPoliciesStep = ({ 
    documents, 
    memoizedHandleDocumentChange
}) => {
    // Create the adapter functions that DocumentUploadSection expects
    const handleFileAdd = (documentType, files) => {
        // Only take the first file if multiple are selected
        const fileToAdd = Array.isArray(files) ? files[0] : files;
        memoizedHandleDocumentChange(documentType, [fileToAdd]);
    };

    const handleFileRemove = (documentType, index) => {
        // Since we only allow one file, we can just set to empty array
        memoizedHandleDocumentChange(documentType, []);
    };

    return (
        <>
            <Card sx={{ mb: 4 }}>
                <CardContent>
                    <Typography variant="h5" gutterBottom>
                        Documents and Policies
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                        Upload the following documents for your venue. Documents marked with * are required. Only one document can be uploaded per type.
                    </Typography>
                    
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <DocumentUploadSection
                                documentType="insurance"
                                description="General Liability, Automobile Insurance (if transporting goods), Worker's compensation"
                                files={documents.insurance?.files || []}
                                onFileAdd={handleFileAdd}
                                onFileRemove={handleFileRemove}
                                isRequired={true}
                                title="Insurance Certificates"
                                acceptedFileTypes={['.pdf', '.jpg', '.jpeg', '.png']}
                                maxFiles={1}
                            />
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                            <DocumentUploadSection
                                documentType="healthSafety"
                                description="Health department certificates, fire safety inspection reports"
                                files={documents.healthSafety?.files || []}
                                onFileAdd={handleFileAdd}
                                onFileRemove={handleFileRemove}
                                isRequired={true}
                                title="Proof of Health and Safety Compliance"
                                acceptedFileTypes={['.pdf', '.jpg', '.jpeg', '.png']}
                                maxFiles={1}
                            />
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                            <DocumentUploadSection
                                documentType="parkingAccess"
                                description="Defines where vendors can use load docks and which entrances they can use"
                                files={documents.parkingAccess?.files || []}
                                onFileAdd={handleFileAdd}
                                onFileRemove={handleFileRemove}
                                isRequired={true}
                                title="Parking and Access Agreements"
                                acceptedFileTypes={['.pdf', '.doc', '.docx']}
                                maxFiles={1}
                            />
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                            <DocumentUploadSection
                                documentType="liabilityWaivers"
                                description="Standard liability waivers for vendors and event participants"
                                files={documents.liabilityWaivers?.files || []}
                                onFileAdd={handleFileAdd}
                                onFileRemove={handleFileRemove}
                                isRequired={true}
                                title="Liability Waivers"
                                acceptedFileTypes={['.pdf', '.doc', '.docx']}
                                maxFiles={1}
                            />
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                            <DocumentUploadSection
                                documentType="noiseGuidelines"
                                description="Noise restrictions, curfew times for DJs and music"
                                files={documents.noiseGuidelines?.files || []}
                                onFileAdd={handleFileAdd}
                                onFileRemove={handleFileRemove}
                                isRequired={false}
                                title="Noise and Environment Guidelines"
                                acceptedFileTypes={['.pdf', '.doc', '.docx']}
                                maxFiles={1}
                            />
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                            <DocumentUploadSection
                                documentType="equipmentApproval"
                                description="Guidelines for equipment setup and approval process"
                                files={documents.equipmentApproval?.files || []}
                                onFileAdd={handleFileAdd}
                                onFileRemove={handleFileRemove}
                                isRequired={false}
                                title="Equipment and Setup Approval"
                                acceptedFileTypes={['.pdf', '.doc', '.docx']}
                                maxFiles={1}
                            />
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                            <DocumentUploadSection
                                documentType="nonCompete"
                                description="Non-Compete / Non-Solicitation Agreement (Optional)"
                                files={documents.nonCompete?.files || []}
                                onFileAdd={handleFileAdd}
                                onFileRemove={handleFileRemove}
                                isRequired={false}
                                title="Non-Compete Agreement"
                                acceptedFileTypes={['.pdf', '.doc', '.docx']}
                                maxFiles={1}
                            />
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                            <DocumentUploadSection
                                documentType="subcontractor"
                                description="Agreement for subcontractors (if applicable)"
                                files={documents.subcontractor?.files || []}
                                onFileAdd={handleFileAdd}
                                onFileRemove={handleFileRemove}
                                isRequired={false}
                                title="Subcontractor Agreement"
                                acceptedFileTypes={['.pdf', '.doc', '.docx']}
                                maxFiles={1}
                            />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
        </>
    );
};